'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function SessionHero() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const scrollToForm = () => {
    if (!isHydrated) return;
    const formSection = document.getElementById('registration-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-sky-light/10 to-accent/5 pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full border-2 border-green-500/30">
              <Icon name="AcademicCapIcon" size={20} variant="solid" className="text-green-600" />
              <span className="text-sm font-bold text-green-600 uppercase tracking-wide">100% FREE Session for Colleges</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
              AI & Job Awareness Program
              <span className="block mt-2 bg-gradient-to-r from-primary via-accent to-sky-light bg-clip-text text-transparent">
                For Students & Graduates
              </span>
            </h1>

            <p className="text-lg text-muted-foreground font-body">
              A <span className="font-semibold text-foreground">FREE 90-minute interactive session</span> designed to give students and graduates 
              clear career clarity, realistic expectations, and a practical preparation roadmap in the age of AI.
            </p>

            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">90 Minutes Session</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="UserGroupIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Interactive Format</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CpuChipIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">AI & Industry Focus</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">Career Roadmap</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                onClick={scrollToForm}
                className="px-10 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-lg shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Book FREE Session Now</span>
                <Icon name="ArrowRightIcon" size={20} variant="solid" />
              </button>
              <button
                onClick={() => isHydrated && window.scrollTo({ top: document.getElementById('program-details')?.offsetTop || 0, behavior: 'smooth' })}
                className="px-8 py-4 bg-card border-2 border-primary text-primary rounded-lg font-headline font-semibold text-lg hover:bg-primary/5 transition-all duration-300"
              >
                Learn More
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-border">
              <div>
                <div className="text-2xl font-headline font-bold text-primary">100+</div>
                <div className="text-sm text-muted-foreground">Colleges Reached</div>
              </div>
              <div>
                <div className="text-2xl font-headline font-bold text-primary">5000+</div>
                <div className="text-sm text-muted-foreground">Students Trained</div>
              </div>
              <div>
                <div className="text-2xl font-headline font-bold text-primary">4.9★</div>
                <div className="text-sm text-muted-foreground">Session Rating</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-sky-lg">
              <AppImage
                src="/assets/images/college-session-hero.png"
                alt="Students attending interactive AI awareness session"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full shadow-lg animate-float">
              <div className="text-2xl font-bold">FREE</div>
              <div className="text-xs">For Colleges</div>
            </div>

            {/* Stats Badge */}
            <div className="absolute -bottom-4 -left-4 bg-card border-2 border-primary rounded-xl px-6 py-4 shadow-lg">
              <div className="text-sm text-muted-foreground mb-1">Session Duration</div>
              <div className="text-2xl font-headline font-bold text-primary">90 Minutes</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
